import { BreakPoint } from '@adornis/base/client/frontend-helpers/window-size.js';
import { logger } from '@adornis/base/logging.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import type { TranslationDictionaryBuddy } from '@adornis/bh-matching-translation/translations.js';
import { MatchingUserAPI } from '@adornis/bh-matching-user/api/MatchingUserAPI.js';
import { type BuddyUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { Bold } from '@adornis/tiptap/client/extensions/Bold.js';
import { Document } from '@adornis/tiptap/client/extensions/Document.js';
import { Dropcursor } from '@adornis/tiptap/client/extensions/Dropcursor.js';
import { HardBreak } from '@adornis/tiptap/client/extensions/HardBreak.js';
import { Italic } from '@adornis/tiptap/client/extensions/Italic.js';
import { Link } from '@adornis/tiptap/client/extensions/Link/Link.js';
import { Paragraph } from '@adornis/tiptap/client/extensions/Paragraph.js';
import { Strike } from '@adornis/tiptap/client/extensions/Strike.js';
import { Text } from '@adornis/tiptap/client/extensions/Text.js';
import { TextStyle } from '@adornis/tiptap/client/extensions/TextStyle.js';
import { TransformerHTML } from '@adornis/tiptap/client/extensions/TransformerHTML.js';
import { Underline } from '@adornis/tiptap/client/extensions/Underline.js';
import type { XTiptap } from '@adornis/tiptap/client/x-tiptap.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { userFieldBlackList } from '../user-field-blacklist.js';

/*
 * @element buddy-user-details
 */
@customElement('buddy-user-details')
export class BuddyUserDetails extends ChemistryLitElement {
  @property({ attribute: false }) user = new RXController<BuddyUser | null>(this, null);
  @property({ attribute: 'always-open', type: Boolean }) alwaysOpen?: boolean;
  @property({ attribute: 'hide-toggle', type: Boolean }) hideToggle?: boolean;

  @property({ attribute: false }) fieldBlackList?: string[];

  @state() protected readonly _translator = new TranslationController<TranslationDictionaryBuddy>(this);
  @state() private readonly _size = new ComponentSizeController(this);
  @state() protected _isOpen = false;

  @state() private _translation = new TranslationController(this);

  override render() {
    return html`
      <x-flex space="md" h100 flex @click=${() => (this._isOpen = true)} ${css({ position: 'relative' })}>
        ${this._isOpen || this.alwaysOpen
          ? html`
              ${this._userDetails()}
              <x-hr></x-hr>
              ${this._note()}
            `
          : html` ${this._userDetails(true)} `}
        ${this.alwaysOpen || this.hideToggle
          ? nothing
          : html`
              ${!!this.user.value?.note?.replace(/(<([^>]+)>)/gi, '')
                ? html`
                    <x-icon
                      ${css({
                        fontSize: '18px',
                        position: 'absolute',
                        top: '3px',
                        right: '25px',
                        color: this.colors.tone.warning,
                        opacity: '.6',
                      })}
                      tone
                    >
                      note
                      <x-tooltip
                        .text=${html` <x-text> ${this._translation.translate('user', 'note_exists')} </x-text> `}
                      ></x-tooltip>
                    </x-icon>
                  `
                : nothing}

              <x-button
                mode="icon"
                icon=${this._isOpen ? 'expand_more' : 'chevron_right'}
                ${css({ position: 'absolute', top: '0', right: '0' })}
                @click=${(e: MouseEvent) => {
                  e.stopPropagation();
                  this._isOpen = !this._isOpen;
                }}
              >
              </x-button>
            `}
      </x-flex>
    `;
  }

  protected _userDetails(onlyFirstThree?: boolean) {
    const user = this.user.value;
    if (!user) return html` <tr-text> 404 - No user found! </tr-text> `;

    //const render = rena
    const allFields = Object.values((this.user.value?.constructor as typeof BuddyUser).fields).filter(
      field => !(this.fieldBlackList ?? userFieldBlackList).includes(field.name),
    );
    const previewFields = ['firstName', 'lastName', 'gender'];
    let fields = allFields;

    if (onlyFirstThree)
      fields = allFields
        .filter(curField => previewFields.includes(curField.name))
        .sort((fieldA, fieldB) => previewFields.indexOf(fieldA.name) - previewFields.indexOf(fieldB.name));

    return html`
      <x-flex flex space="sm">
        <x-grid space="xs" flex columns=${this._size.componentDevice >= BreakPoint.phone ? '1fr 2fr' : '1'}>
          ${repeat(fields, field => this._userDetailsField(field, user[field.name]))}
        </x-grid>
      </x-flex>
    `;
  }

  protected _userDetailsField(field: BaseQLFieldDefinition, value: any) {
    const getFieldTranslation = (field: BaseQLFieldDefinition, value: any) => {
      const fieldType = field.type();
      if (fieldType._isBaseQLEnum) {
        if (Array.isArray(value)) return value.map(v => getFieldTranslation(field, v)).join(', ');
        const translation = this._translator.translate('enums', value);
        return !translation.startsWith('enums.') ? translation : value;
      }
      return value;
    };
    return html`
      <tr-text bold> ${this._translator.translate('field', field.name)} </tr-text>
      <tr-text ${css({ overflowWrap: 'break-word', wordBreak: 'break-word' })}>
        ${value ? getFieldTranslation(field, value) : '-'}
      </tr-text>
    `;
  }

  protected _noteEl = createRef<XTiptap>();
  protected _note() {
    const user = this.user.value;
    if (!user) return nothing;

    const extensions = [
      [TransformerHTML, Document, Text, TextStyle, HardBreak, Dropcursor, Bold, Italic, Underline, Strike, Paragraph()],
      [Link],
    ];
    return html`
      <x-flex space="xs">
        <x-text bold> ${this._translator.translate('buddy', 'note')} </x-text>
        <x-tiptap
          placeholder=${this._translator.translate('buddy', 'note')}
          .extensions=${extensions}
          .value=${user.note}
          ${ref(this._noteEl)}
        ></x-tiptap>
        <x-flex horizontal end>
          <x-button mode="text" icon="save" @click=${() => this._saveNote()}>
            ${this._translator.translate('buddy', 'saveNote')}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private async _saveNote() {
    const note = this._noteEl.value?.value.value as string;
    if (note && this.user.value && note !== this.user.value.note) {
      try {
        await TrDialog.waitFor(MatchingUserAPI.saveNote(this.user.value._id, note)());
        void XSnackbar.show('Notizen wurden erfolgreich gespeichert.');
      } catch (err) {
        logger.error({ err });
        void TrDialog.alert('Es gab ein Problem beim Speichern der Notiz. Bitte versuchen Sie es später erneut.');
      }
    }
  }
}
